import { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  doc,
} from "firebase/firestore";
import { db } from "../../lib/firebase";
import { Link } from "react-router-dom";
import { STATUS_OPTIONS } from "./StatusOptions";
import { useNavigate } from "react-router-dom";

function formatDate(ts) {
  if (!ts) return "—";
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString();
}

function removeText(str, n = 160) {
  if (!str) return "";
  const s = String(str).trim();
  return s.length > n ? s.slice(0, n) + "": s;
}

function getMillis(ts) {
  if (!ts) return 0;
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  return d.getTime();
}

function truncate(str, n = 140) {
  if (!str) return "—";
  const s = String(str).trim();
  return s.length > n ? s.slice(0, n) + "…" : s;
}

const SORTS = [
  { key: "created_desc", label: "Newest first" },
  { key: "created_asc", label: "Oldest first" },
  { key: "updated_desc", label: "Last updated" },
  { key: "note_desc", label: "Last note" },
];

export default function AdminDashboard() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);

  const [qtext, setQtext] = useState("");
  const [sortKey, setSortKey] = useState("created_desc");

  const nav = useNavigate();
    const [createBusy, setCreateBusy] = useState(false);

  /* -------------------- Load requests -------------------- */
  useEffect(() => {
    const q = query(
      collection(db, "evaluationRequests"),
      orderBy("createdAt", "desc"),
      limit(250)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setItems(rows);
        setLoading(false);
      },
      () => {
        setItems([]);
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  /* -------------------- Filter + sort -------------------- */
  const filteredAndSorted = useMemo(() => {
    const needle = qtext.trim().toLowerCase();

    let arr = items.filter((r) => {
      if (!needle) return true;

      const blob = [
        r.name,
        r.email,
        r.phone,
        r.message,
        r.lastNoteText,
        r.status,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return blob.includes(needle);
    });

    arr = [...arr].sort((a, b) => {
      const aCreated = getMillis(a.createdAt);
      const bCreated = getMillis(b.createdAt);

      const aUpdated = getMillis(a.lastUpdatedAt) || aCreated;
      const bUpdated = getMillis(b.lastUpdatedAt) || bCreated;

      const aNote = getMillis(a.lastNoteAt);
      const bNote = getMillis(b.lastNoteAt);

      switch (sortKey) {
        case "created_asc":
          return aCreated - bCreated;
        case "updated_desc":
          return bUpdated - aUpdated;
        case "note_desc":
          if (!aNote && !bNote) return bUpdated - aUpdated;
          if (!aNote) return 1;
          if (!bNote) return -1;
          return bNote - aNote;
        case "created_desc":
        default:
          return bCreated - aCreated;
      }
    });

    return arr;
  }, [items, qtext, sortKey]);

  /* -------------------- Quick status update -------------------- */
  async function quickSetStatus(requestId, nextStatus) {
    await updateDoc(doc(db, "evaluationRequests", requestId), {
      status: nextStatus,
      lastUpdatedAt: serverTimestamp(),
    });

    await addDoc(collection(db, "evaluationRequests", requestId, "notes"), {
      createdAt: serverTimestamp(),
      text: `Status changed to: ${nextStatus}`,
      system: true,
    });
  }

  async function createManualRequest() {
  setCreateBusy(true);
  try {
    const ref = await addDoc(collection(db, "evaluationRequests"), {
      source: "manual",
      status: "new",
      createdAt: serverTimestamp(),
      lastUpdatedAt: serverTimestamp(),

      // placeholders — fill in later on RequestDetail (or we can add quick edit there)
      name: "",
      email: "",
      phone: "",
      message: "",
      attachments: [],

      clientId: null,
    });

    nav(`/admin/requests/${ref.id}`);
  } finally {
    setCreateBusy(false);
  }
}


  /* -------------------- UI -------------------- */
  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "flex-start",
        }}
      >
        <button className="btn primary" onClick={createManualRequest} disabled={createBusy}>
        {createBusy ? "Creating…" : "Create New"}
        </button>
        <div style={{ display: "grid", gap: 10, minWidth: 320 }}>
          <input
            type="text"
            placeholder="Search name, email, phone, message, notes…"
            value={qtext}
            onChange={(e) => setQtext(e.target.value)}
            style={{
              width: "100%",
              padding: "10px 12px",
              borderRadius: 12,
              border: "1px solid rgba(0,0,0,0.15)",
              background: "rgba(255,255,255,0.7)",
            }}
          />

          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
            style={{
              width: "100%",
              padding: "10px 12px",
              borderRadius: 12,
              border: "1px solid rgba(0,0,0,0.15)",
              background: "rgba(255,255,255,0.7)",
            }}
          >
            {SORTS.map((s) => (
              <option key={s.key} value={s.key}>
                Sort: {s.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading && <p className="muted">Loading…</p>}

      {!loading && filteredAndSorted.length === 0 && (
        <p className="muted" style={{ marginTop: 12 }}>
          No requests match your search.
        </p>
      )}

      {!loading && filteredAndSorted.length > 0 && (
        <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
          {filteredAndSorted.map((r) => {
            const status = r.status || "new";
            const attachmentCount = Array.isArray(r.attachments)
              ? r.attachments.length
              : 0;

            return (
              <article
                key={r.id}
                style={{
                  border: "1px solid rgba(0,0,0,0.1)",
                  borderRadius: 14,
                  padding: 14,
                  background: "rgba(255,255,255,0.7)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    flexWrap: "wrap",
                    alignItems: "flex-start",
                  }}
                >
                  <div style={{ minWidth: 260 }}>
                    <strong>{r.name || "—"}</strong>
                    <div className="micro muted" style={{ marginTop: 4 }}>
                      {r.email || "—"} • {r.phone || "—"}
                    </div>
                  </div>

                  <div style={{ textAlign: "right", minWidth: 240 }}>

                    <div style={{ marginTop: 10 }}>
                      <select
                        value={status}
                        onChange={(e) => quickSetStatus(r.id, e.target.value)}
                        style={{
                          width: "100%",
                          padding: "10px 12px",
                          borderRadius: 12,
                          border: "1px solid rgba(0,0,0,0.15)",
                          background: "rgba(255,255,255,0.7)",
                        }}
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {r.message && (
                  <p style={{ lineHeight: "1rem", margin: "10px 0 auto 0" }}>
                    {String(r.message).length > 180
                      ? String(r.message).slice(0, 180) + "…"
                      : r.message}
                  </p>
                )}

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 10,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <div className="micro muted">
                    Photos: {attachmentCount}
                  </div>

                  <Link className="btn primary" to={`/admin/requests/${r.id}`}>
                    View
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
